/**
 * The marker itself. `Symbol.for` rather than a module-local symbol, so a value marked in one bundle
 * is still recognized by a filter loaded from another copy of this module.
 */
const TELEMETRY_SAFE = Symbol.for('sigma.telemetry.safe');
/**
 * Declares a structure safe to log verbatim, exempting it from the scalar rule in
 * `filterTelemetryValue`.
 *
 * The rule collapses every non-scalar value because PHI only ever reaches a payload inside a
 * structure. That is right by default and wrong for the rare payload that IS a structure of
 * identifiers — a per-record correlation list, where flattening into parallel scalar arrays loses
 * exactly the association (this UID belongs to that patient) the line is logged for.
 *
 * This is an assertion by the call site, not a check: nothing here inspects the contents. Mark only
 * a structure you built yourself out of identifiers, counts, flags and enum values — never a domain
 * object, a provider response, or anything you received rather than assembled. It is a hole in the
 * boundary by design, so it is deliberately verbose to reach for, and every use is reviewable by
 * grepping this function.
 *
 * The value is frozen because a marked structure has been declared safe as it stands; code that
 * later pushed a name into it would silently widen that declaration. The freeze is shallow — the
 * marker travels with the value it was applied to, and nothing about it is inherited by whatever the
 * value happens to reference.
 *
 * See `docs/decisions/phi-telemetry-boundary.md` and `.claude/skills/compliance/SKILL.md` (Rule 2).
 */
export function markTelemetrySafe(value) {
    if (isTelemetrySafe(value)) {
        return value;
    }
    Object.defineProperty(value, TELEMETRY_SAFE, { value: true, enumerable: false });
    return Object.freeze(value);
}
/**
 * Whether `markTelemetrySafe` was applied to this exact value. Symbol keys are invisible to
 * `Object.entries` and to `JSON.stringify`, so the marker never appears in the line it authorizes.
 * @__NO_SIDE_EFFECTS__
 */
export function isTelemetrySafe(value) {
    return typeof value === 'object' && value !== null && TELEMETRY_SAFE in value;
}
//# sourceMappingURL=telemetrySafe.js.map